/** Canvas helpers for turning a crop selection into an upload-ready logo file. */

export const LOGO_CROP_OUTPUT_SIZE = 512;

export interface CropAreaPixels {
  x: number;
  y: number;
  width: number;
  height: number;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () => reject(new Error("Could not load the selected image.")));
    image.crossOrigin = "anonymous";
    image.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not prepare the cropped image."));
          return;
        }
        resolve(blob);
      },
      type,
      quality,
    );
  });
}

function resolveOutputMimeType(sourceType: string): { mimeType: string; extension: string; quality?: number } {
  const normalized = sourceType.toLowerCase();
  if (normalized === "image/png") {
    return { mimeType: "image/png", extension: "png" };
  }
  return { mimeType: "image/jpeg", extension: "jpg", quality: 0.92 };
}

/**
 * Draws the cropped region onto a square canvas and returns a File sized for logo upload.
 */
export async function createCroppedLogoFile(
  imageSrc: string,
  crop: CropAreaPixels,
  sourceFileName: string,
  sourceType: string,
): Promise<File> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = LOGO_CROP_OUTPUT_SIZE;
  canvas.height = LOGO_CROP_OUTPUT_SIZE;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("Could not prepare the cropped image.");
  }

  context.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    LOGO_CROP_OUTPUT_SIZE,
    LOGO_CROP_OUTPUT_SIZE,
  );

  const { mimeType, extension, quality } = resolveOutputMimeType(sourceType);
  const blob = await canvasToBlob(canvas, mimeType, quality);
  const baseName = sourceFileName.replace(/\.[^.]+$/, "") || "logo";
  return new File([blob], `${baseName}-cropped.${extension}`, { type: mimeType });
}
